import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  guestBookGuestQueryKey,
  restaurantGuestsQueryKey,
} from 'restaurantAdmin/guestBook/hooks';
import {
  getGuestVipTier,
  type GuestsApiGuest,
  updateGuestVipTier,
} from 'restaurantAdmin/guestBook/apiHelpers';
import {
  type InfiniteData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export const guestVipTierQueryKey = (restaurantId: string, guestId: string) =>
  ['restaurants', restaurantId, 'guests', guestId, 'vipTier'] as const;

export const useGuestVipTier = (guestId: string) => {
  const { id: restaurantId } = useRestaurant();

  return useQuery({
    queryKey: guestVipTierQueryKey(restaurantId, guestId),
    queryFn: async () => getGuestVipTier(restaurantId, guestId),
  });
};

export const useUpdateGuestVipTier = (guestId: string) => {
  const { id: restaurantId } = useRestaurant();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vipTierId: string | null) =>
      updateGuestVipTier({ restaurantId, guestId, vipTierId }),
    onSuccess: async (_data, vipTierId) => {
      const hasVipTier = vipTierId !== null;

      // Patch the cached guest in place instead of invalidating the guestbook
      // list, which would refetch every page the admin has scrolled through.
      queryClient.setQueriesData<InfiniteData<GuestsApiGuest[]>>(
        { queryKey: restaurantGuestsQueryKey(restaurantId) },
        (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page) =>
              page.map((guest) =>
                guest.id === guestId ? { ...guest, hasVipTier } : guest,
              ),
            ),
          };
        },
      );

      queryClient.setQueryData<GuestsApiGuest>(
        guestBookGuestQueryKey(restaurantId, guestId),
        (guest) => (guest ? { ...guest, hasVipTier } : guest),
      );

      await queryClient.invalidateQueries({
        queryKey: guestVipTierQueryKey(restaurantId, guestId),
      });
    },
  });
};
